/**
 * Reconnect sequence (spec §16.2): session check → register offline-created pages → (rooms
 * rejoin on their own) → resume uploads → invalidate server caches.
 */
import type { QueryClient } from "@tanstack/react-query";
import { fetchMe, qk } from "../api/hooks";
import { drain } from "./uploadQueue";
import { registerPendingPages } from "./pendingPages";

let installed = false;

export function installReconnect(queryClient: QueryClient, onSessionLost: () => void): void {
  if (installed) return;
  installed = true;
  const run = async () => {
    try {
      const had = queryClient.getQueryData(qk.me);
      const me = await fetchMe();
      if (me === null) {
        // only a *lost* session sends the user to the login page: a check that races a fresh
        // sign-in (or runs on the login page itself) must not redirect
        if (had) onSessionLost();
        return;
      }
      queryClient.setQueryData(qk.me, me);
      await registerPendingPages();
      await drain();
      await queryClient.invalidateQueries();
    } catch (e) {
      console.debug("[reconnect] deferred", e);
    }
  };
  window.addEventListener("online", () => void run());
  // also attempt shortly after start (a page load while online counts as a reconnect)
  if (navigator.onLine) setTimeout(() => void run(), 500);
}
